import { useTranslation } from 'react-i18next';
import { Badge, Field, Panel } from '@/shared/ui';
import { TextField } from '@/shared/forms';
import { cn } from '@/shared/lib/utils';
import { STATION_CHAIN_COLORS } from '../stationChainColors';
import { STATION_CHAIN_STATUS_ORDER } from '../stationChainStatus';
import type { StationChainFormApi } from '../useStationChainForm';

const LABEL_W = 104;

export interface StationChainIdentityPanelProps {
  form: StationChainFormApi;
  title: string;
}

/** Sbalitelný panel identity trasy — název, kód, barva v mapě, stav + odznak směru (Tam/Zpět). */
export function StationChainIdentityPanel({ form, title }: StationChainIdentityPanelProps) {
  const { t } = useTranslation('products');

  return (
    <Panel
      variant="filled"
      icon="bus"
      collapsible
      title={title}
      subtitle={
        <form.Subscribe selector={(state) => state.values.code}>
          {(code) => (code ? <span className="text-phx-sm font-mono">{code}</span> : null)}
        </form.Subscribe>
      }
      badge={
        <form.Subscribe selector={(state) => state.values.stops[0]?.stationIsDomestic}>
          {(firstIsDomestic) => (
            <Badge tone={firstIsDomestic === false ? 'neutral' : 'info'}>
              {firstIsDomestic === false
                ? t('stationChain.direction.return')
                : t('stationChain.direction.outbound')}
            </Badge>
          )}
        </form.Subscribe>
      }
    >
      <div className="gap-phx-3 flex flex-col">
        <form.Field name="name">
          {(field) => (
            <TextField
              field={field}
              layout="inline"
              labelWidth={LABEL_W}
              label={t('stationChain.name.label')}
              required
              placeholder={t('stationChain.name.placeholder')}
              data-testid="station-chain-name"
            />
          )}
        </form.Field>

        <form.Field name="code">
          {(field) => (
            <TextField
              field={field}
              layout="inline"
              labelWidth={LABEL_W}
              label={t('stationChain.code.label')}
              placeholder="PRG-BTS"
            />
          )}
        </form.Field>

        <form.Field name="color">
          {(field) => (
            <Field layout="inline" labelWidth={LABEL_W} label={t('stationChain.color.label')}>
              <div className="gap-phx-2 flex flex-wrap">
                {STATION_CHAIN_COLORS.map((color) => {
                  const active = field.state.value === color;
                  return (
                    <button
                      key={color}
                      type="button"
                      onClick={() => field.handleChange(color)}
                      aria-label={color}
                      aria-pressed={active}
                      className={cn(
                        'flex h-6 w-6 items-center justify-center rounded-full border-2',
                        active ? 'border-phx-text' : 'border-transparent'
                      )}
                      style={{ backgroundColor: color }}
                    >
                      {active ? <span className="text-[11px] leading-none text-white">✓</span> : null}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}
        </form.Field>

        <form.Field name="status">
          {(field) => (
            <Field layout="inline" labelWidth={LABEL_W} label={t('stationChain.status.label')}>
              <div className="border-phx-border-2 rounded-phx inline-flex w-fit overflow-hidden border">
                {STATION_CHAIN_STATUS_ORDER.map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => field.handleChange(status)}
                    className={cn(
                      'px-phx-3 py-phx-1 text-phx-sm',
                      field.state.value === status
                        ? 'bg-phx-primary text-white'
                        : 'bg-phx-surface text-phx-text'
                    )}
                  >
                    {t(`stationChain.status.${status}`)}
                  </button>
                ))}
              </div>
            </Field>
          )}
        </form.Field>
      </div>
    </Panel>
  );
}
